import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MessageCircle, ArrowUpRight, Mail } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { RevealText, FadeUp, Counter } from "@/components/site/Reveal";
import { IMAGES } from "@/data/images";

const Home = () => {
  const { t, dir, lang } = useLang();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yFalcon = useTransform(scrollYProgress, [0, 1], ["0%", "-25%"]);
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "-10%"]);
  const opacityHero = useTransform(scrollYProgress, [0, 0.9], [1, 0.6]);

  return (
    <div data-testid="home-page">
      {/* ===== HERO ===== */}
      <section ref={heroRef} className="relative min-h-[100svh] pt-24 md:pt-28 overflow-hidden">
        {/* Background parallax */}
        <motion.div style={{ y: yBg }} className="absolute inset-0 -z-10">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-60"
            style={{ backgroundImage: `url(${IMAGES.hero_parallax})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[color:var(--sw-bg)]/80 via-[color:var(--sw-bg)]/70 to-[color:var(--sw-bg)]" />
        </motion.div>

        {/* Falcon SVG parallax */}
        <motion.div style={{ y: yFalcon, opacity: opacityHero }} className="pointer-events-none absolute inset-x-0 top-24 md:top-16 flex justify-center -z-[5]">
          <FalconSilhouette className="w-[520px] md:w-[880px] opacity-25" />
        </motion.div>

        <div className="mx-auto max-w-[1440px] px-6 md:px-10 pt-10 md:pt-20 pb-20 md:pb-32 relative">
          {/* Eyebrow ticker */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.9 }}
            className="flex items-center gap-4 text-[11px] uppercase tracking-[0.32em] text-white/60"
          >
            <span className="w-8 h-px bg-[color:var(--sw-orange)]" />
            <span>{t.home.chapter} 01 — Riyadh · Saudi Arabia</span>
          </motion.div>

          {/* Massive kinetic headline */}
          <div className="mt-8 md:mt-14 max-w-[1200px]">
            <RevealText
              lines={[t.home.hero.line1, t.home.hero.line2, t.home.hero.line3]}
              as="h1"
              className={`display text-white tracking-tight ${
                lang === "ur"
                  ? "text-xl sm:text-2xl md:text-3xl lg:text-4xl"
                  : lang === "en"
                    ? "text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02]"
                    : "text-5xl sm:text-6xl md:text-7xl lg:text-[7.5rem] leading-[0.88]"
              }`}
            />
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <FadeUp delay={0.8} className="md:col-span-6">
              <p className="text-white/70 text-base md:text-lg max-w-xl leading-relaxed">{t.home.hero.sub}</p>
            </FadeUp>

            <FadeUp delay={0.95} className="md:col-span-6 flex flex-wrap gap-3 md:justify-end">
              <Link to="/contact" data-testid="hero-cta-request" className="btn-primary px-6 py-3.5 rounded-full inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-semibold">
                {t.home.hero.cta1} <ArrowUpRight size={16} />
              </Link>
              <a href="https://wa.me/966590960314" target="_blank" rel="noreferrer" data-testid="hero-cta-whatsapp" className="btn-ghost px-6 py-3.5 rounded-full inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em]">
                <MessageCircle size={16} /> {t.home.hero.cta2}
              </a>
              <a href="tel:+966590960314" target="_blank" rel="noreferrer" data-testid="hero-cta-call" className="btn-ghost px-6 py-3.5 rounded-full inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em]">
                <Phone size={16} /> {t.home.hero.cta3}
              </a>
            </FadeUp>
          </div>

          {/* Scroll indicator */}
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}
            className="absolute bottom-6 inset-x-0 flex justify-center text-white/40 text-[10px] uppercase tracking-[0.4em]"
          >
            <div className="flex flex-col items-center gap-2">
              <span>Scroll</span>
              <span className="block w-px h-8 bg-white/20 overflow-hidden relative">
                <motion.span animate={{ y: [-32, 32] }} transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }} className="absolute inset-x-0 top-0 h-3 bg-[color:var(--sw-orange)]" />
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== EDITORIAL MARQUEE ===== */}
      <section className="border-y border-white/10 py-6 md:py-8 overflow-hidden">
        <div className="sw-marquee-track whitespace-nowrap flex gap-12" style={{ animationDuration: "50s" }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="display text-4xl md:text-6xl text-white/40 flex items-center gap-12">
              {t.home.marquee}
            </div>
          ))}
        </div>
      </section>

      {/* ===== INTRO / MANIFESTO ===== */}
      <section className="mx-auto max-w-[1440px] px-6 md:px-10 py-24 md:py-40">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <FadeUp className="md:col-span-4">
            <div className="chapter-num text-[110px] md:text-[160px] leading-none">01</div>
            <div className="text-[11px] uppercase tracking-[0.32em] text-white/50 mt-4">{t.home.intro.eyebrow}</div>
          </FadeUp>
          <div className="md:col-span-8 space-y-8">
            <RevealText lines={[t.home.intro.title]} as="h2" className="display text-4xl md:text-6xl leading-[1.02] text-white" />
            <FadeUp delay={0.15}>
              <p className={`text-white/70 leading-relaxed max-w-3xl ${lang === "ur" ? "text-lg" : "text-lg md:text-xl"}`}>{t.home.intro.body}</p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ===== SERVICES CARDS ===== */}
      <section className="mx-auto max-w-[1440px] px-6 md:px-10 pb-24 md:pb-40">
        <div className="flex items-end justify-between mb-10 md:mb-16 gap-8 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.32em] text-white/50 mb-4">{t.home.services.eyebrow}</div>
            <RevealText lines={[t.home.services.title]} as="h2" className="display text-3xl md:text-5xl text-white leading-tight" />
          </div>
          <Link to="/services" className="edit-link text-white/80 hover:text-[color:var(--sw-orange)] text-[12px] uppercase tracking-[0.24em]" data-testid="home-view-all-services">
            {t.nav.services} →
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {t.home.services.cards.map((c, i) => (
            <FadeUp key={i} delay={i * 0.08}>
              <Link to={c.to} data-testid={`home-service-card-${i}`} className="sw-card block rounded-md p-8 h-full group relative overflow-hidden">
                <div className="chapter-num text-[42px] leading-none opacity-70">0{i + 1}</div>
                <div className="mt-14 space-y-3">
                  <div className="display text-2xl text-white group-hover:text-[color:var(--sw-orange)] transition-colors">{c.t}</div>
                  <div className="text-white/60 text-sm leading-relaxed">{c.d}</div>
                </div>
                <ArrowUpRight className="absolute top-6 end-6 text-white/40 group-hover:text-[color:var(--sw-orange)] group-hover:rotate-45 transition-transform" size={18} />
              </Link>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* ===== MOST REQUESTED + IMAGE SPOT ===== */}
      <section className="mx-auto max-w-[1440px] px-6 md:px-10 pb-24 md:pb-40">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <FadeUp className="md:col-span-5">
            <div className="spot-image rounded-md overflow-hidden aspect-[3/4]">
              <img src={IMAGES.macro_eagle} alt="Eagle detail" className="w-full h-full object-cover grayscale-[30%] contrast-125" />
            </div>
          </FadeUp>
          <div className="md:col-span-7 flex flex-col justify-center">
            <div className="text-[11px] uppercase tracking-[0.32em] text-white/50 mb-4">{t.home.popular.eyebrow}</div>
            <RevealText lines={[t.home.popular.title]} as="h2" className="display text-3xl md:text-5xl text-white leading-tight mb-10" />
            <ul className="space-y-4">
              {t.home.popular.list.map((s, i) => (
                <FadeUp key={i} delay={i * 0.06}>
                  <li className="flex items-baseline gap-6 border-b border-white/10 pb-4 group">
                    <span className="chapter-num text-sm w-8">0{i + 1}</span>
                    <span className={`text-white/85 group-hover:text-[color:var(--sw-orange)] transition-colors ${lang === "ur" ? "text-base" : "text-lg md:text-xl"}`}>{s}</span>
                  </li>
                </FadeUp>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ===== WHY US ===== */}
      <section className="border-t border-white/10 bg-[color:var(--sw-surface)]">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-24 md:py-40">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-12 md:mb-20">
            <FadeUp className="md:col-span-5">
              <div className="chapter-num text-[110px] md:text-[160px] leading-none">04</div>
              <div className="text-[11px] uppercase tracking-[0.32em] text-white/50 mt-4">{t.home.why.eyebrow}</div>
            </FadeUp>
            <div className="md:col-span-7 flex items-end">
              <RevealText lines={[t.home.why.title]} as="h2" className="display text-4xl md:text-6xl text-white leading-[1.02]" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {t.home.why.points.map((p, i) => (
              <FadeUp key={i} delay={i * 0.08}>
                <div className="sw-card rounded-md p-8 md:p-10">
                  <div className="flex items-start gap-6">
                    <span className="chapter-num text-3xl md:text-4xl">{p.n}</span>
                    <div>
                      <div className="display text-2xl md:text-3xl text-white mb-3">{p.t}</div>
                      <div className="text-white/60 leading-relaxed">{p.d}</div>
                    </div>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ===== STATS ===== */}
      <section className="mx-auto max-w-[1440px] px-6 md:px-10 py-24 md:py-40">
        <div className="text-[11px] uppercase tracking-[0.32em] text-white/50 mb-10">{t.home.stats.eyebrow}</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
          {t.home.stats.items.map((s, i) => (
            <FadeUp key={i} delay={i * 0.1}>
              <div className="border-t border-white/15 pt-6">
                <div className="display text-4xl md:text-6xl lg:text-7xl text-white leading-none">{s.n}</div>
                <div className="mt-3 text-[11px] uppercase tracking-[0.3em] text-white/50">{s.l}</div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* ===== QUICK CONTACT ===== */}
      <section className="border-t border-white/10">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-center">
            <div className="md:col-span-6">
              <RevealText lines={[t.home.quickContact.title]} as="h3" className="display text-3xl md:text-5xl text-white leading-tight" />
              <div className="text-white/60 mt-4">{t.home.quickContact.sub}</div>
            </div>
            <div className="md:col-span-6 flex flex-wrap gap-3 md:justify-end">
              <a href="tel:+966590960314" target="_blank" rel="noreferrer" className="btn-ghost px-5 py-3 rounded-full inline-flex items-center gap-2" data-testid="quick-call1"><Phone size={16}/> 0590960314</a>
              <a href="tel:+966567522149" target="_blank" rel="noreferrer" className="btn-ghost px-5 py-3 rounded-full inline-flex items-center gap-2" data-testid="quick-call2"><Phone size={16}/> 0567522149</a>
              <a href="https://wa.me/966590960314" target="_blank" rel="noreferrer" className="btn-primary px-5 py-3 rounded-full inline-flex items-center gap-2" data-testid="quick-whatsapp"><MessageCircle size={16}/> WhatsApp</a>
              <a href="mailto:info@smartwayinsa.com" target="_blank" rel="noreferrer" className="btn-ghost px-5 py-3 rounded-full inline-flex items-center gap-2" data-testid="quick-email"><Mail size={16}/> Email</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const FalconSilhouette = ({ className = "" }) => (
  <svg viewBox="0 0 900 560" className={className} fill="none" aria-hidden="true">
    <defs>
      <linearGradient id="fg" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#F26522" stopOpacity="0.6" />
        <stop offset="1" stopColor="#F26522" stopOpacity="0" />
      </linearGradient>
    </defs>
    <g fill="#0F0F10" stroke="#F26522" strokeWidth="1.2" opacity="0.9">
      <path d="M450 60 C 360 200 250 260 90 240 L 260 340 L 120 470 C 280 470 380 500 450 560 C 520 500 620 470 780 470 L 640 340 L 810 240 C 650 260 540 200 450 60 Z" fill="#312D27"/>
      <path d="M425 240 L 420 460 L 480 460 L 475 240 Z" fill="#F26522"/>
      <path d="M430 100 L 470 100 L 460 220 L 440 220 Z" fill="#F26522"/>
    </g>
    <ellipse cx="450" cy="290" rx="380" ry="120" fill="url(#fg)" />
  </svg>
);

export default Home;
